// Delivered / read ticks of my own chat messages (components/chat/MessageMeta).
//
// The backend sends them to the chat room: ChatController.ChatReachedAndRead on a
// send, GroupController.subscribeToGroupChat when a member opens the chat.
//   "isSend" { chats: [{ id, messageReachedToAll }] }
//   "isRead" { chats: [{ id, messageReadByAll }] }   (a 1:1 open sends { id, isRead })
// Every open chat keeps its own listener: Socket.off("isRead") without the handler
// took the listeners of the other open chats away, so only the chat that had
// registered last got its ticks. Ticks only move forward, and a receipt for a row
// the thread does not know yet (my message still under its request id) waits here
// until the row has its server id.

const yes = (v) => v === true || v === "true";

export function createReceiptTracker() {
  const read = new Set();
  const delivered = new Set();
  const add = (set, id) => {
    const key = String(id);
    if (set.has(key)) return false;
    set.add(key);
    return true;
  };
  return {
    /** Records rows; true when a tick is new. kind: "read" | "delivered" | "rows" (getChat rows). */
    note(kind, rows) {
      let changed = false;
      for (const row of Array.isArray(rows) ? rows : []) {
        if (!row || row.id == null) continue;
        const isRead = yes(row.messageReadByAll) || (kind === "read" && yes(row.isRead));
        if (kind !== "delivered" && isRead) changed = add(read, row.id) || changed;
        if (kind !== "read" && yes(row.messageReachedToAll)) changed = add(delivered, row.id) || changed;
      }
      return changed;
    },
    /** The row with the ticks seen so far. */
    apply(row) {
      if (!row || row.id == null) return row;
      const key = String(row.id);
      if (read.has(key) && !yes(row.messageReadByAll)) {
        return { ...row, messageReadByAll: true, messageReachedToAll: true };
      }
      if (delivered.has(key) && !yes(row.messageReachedToAll)) {
        return { ...row, messageReachedToAll: true };
      }
      return row;
    },
  };
}

/**
 * Listens for "isRead" / "isSend" on `socket` into `tracker`; onChange() fires when
 * a tick is new. Returns the unsubscribe function.
 */
export function subscribeReceipts(socket, tracker, onChange) {
  if (!socket || typeof socket.on !== "function" || !tracker) return () => {};
  const onRead = (data) => {
    if (tracker.note("read", data && data.chats)) onChange();
  };
  const onSend = (data) => {
    if (tracker.note("delivered", data && data.chats)) onChange();
  };
  socket.on("isRead", onRead);
  socket.on("isSend", onSend);
  return () => {
    try {
      socket.off("isRead", onRead);
      socket.off("isSend", onSend);
    } catch (_) {
      /* ignore */
    }
  };
}

/**
 * Runs rejoin() when `socket` comes back: a reconnect, or the new socket of a
 * token refresh (fresh on the server: in no chat room, not in LiveChatUsers).
 * `lastRef` holds the last socket seen; pass the same ref on every call.
 * rejoin(isNewSocket) — a new socket also needs its listeners again.
 * Returns the unsubscribe function.
 */
export function onSocketBack(socket, lastRef, rejoin) {
  if (!socket || typeof socket.on !== "function") return () => {};
  const last = lastRef.current;
  lastRef.current = socket;
  if (last && last !== socket) rejoin(true);
  const onConnect = () => rejoin(false);
  socket.on("connect", onConnect);
  return () => {
    try {
      socket.off("connect", onConnect);
    } catch (_) {
      /* ignore */
    }
  };
}
